import { SIGNALING_URL, ICE_SERVERS } from './signalingConfig'

// One-way WebRTC link for "phone as camera". The phone is the SENDER (has the
// camera track and creates the offer); the desktop is the RECEIVER (answers and
// gets the stream via ontrack). The signaling server only relays SDP + ICE.

// Desktop side: join the room, answer the phone's offer, surface the stream.
export function createReceiver(room, { onStream, onState, onOrient } = {}) {
    const ws = new WebSocket(SIGNALING_URL)
    let pc = null
    const state = (s) => onState && onState(s)
    const relay = (data) =>
        ws.readyState === 1 && ws.send(JSON.stringify({ type: 'signal', data }))

    const ensurePc = () => {
        if (pc) return pc
        pc = new RTCPeerConnection({ iceServers: ICE_SERVERS })
        pc.ontrack = (e) => onStream && onStream(e.streams[0])
        pc.onicecandidate = (e) => {
            if (e.candidate) relay({ kind: 'ice', candidate: e.candidate })
        }
        pc.onconnectionstatechange = () => state(pc.connectionState)
        return pc
    }

    ws.onopen = () => {
        ws.send(JSON.stringify({ type: 'join', room }))
        state('waiting')
    }
    ws.onmessage = async (ev) => {
        let msg
        try {
            msg = JSON.parse(ev.data)
        } catch (e) {
            return
        }
        if (msg.type === 'room-full') return state('full')
        if (msg.type !== 'signal') return
        const d = msg.data
        // The phone reports its device orientation out-of-band (no pc needed).
        if (d.kind === 'orient') {
            onOrient && onOrient(d.orientation)
            return
        }
        // A fresh offer means the phone (re)created its peer connection — e.g.
        // after a phone-side reconnect. The old pc can't answer that new offer
        // (mismatched ICE/DTLS), so ontrack would never fire again and the
        // desktop would show a blank feed. Tear the stale pc down and build a
        // new one so the reconnected stream is picked up.
        if (d.kind === 'offer' && pc) {
            try {
                // Detach handlers first so closing the stale pc doesn't fire a
                // spurious "disconnected" that would flicker the UI mid-reconnect.
                pc.onconnectionstatechange = null
                pc.ontrack = null
                pc.onicecandidate = null
                pc.close()
            } catch (e) {
                /* ignore */
            }
            pc = null
        }
        const p = ensurePc()
        try {
            if (d.kind === 'offer') {
                await p.setRemoteDescription(d.sdp)
                const answer = await p.createAnswer()
                await p.setLocalDescription(answer)
                relay({ kind: 'answer', sdp: p.localDescription })
            } else if (d.kind === 'ice' && d.candidate) {
                await p.addIceCandidate(d.candidate)
            }
        } catch (e) {
            /* ignore malformed signaling */
        }
    }
    ws.onerror = () => state('error')
    ws.onclose = () => state('closed')

    return {
        close() {
            try {
                pc && pc.close()
            } catch (e) {
                /* ignore */
            }
            try {
                ws.close()
            } catch (e) {
                /* ignore */
            }
        },
    }
}

// Phone side: join the room, and once the desktop is present create the offer
// carrying the camera track.
export function createSender(room, stream, { onState } = {}) {
    const ws = new WebSocket(SIGNALING_URL)
    let pc = null
    const state = (s) => onState && onState(s)
    const relay = (data) =>
        ws.readyState === 1 && ws.send(JSON.stringify({ type: 'signal', data }))

    const makeOffer = async () => {
        if (pc) return
        pc = new RTCPeerConnection({ iceServers: ICE_SERVERS })
        pc.onicecandidate = (e) => {
            if (e.candidate) relay({ kind: 'ice', candidate: e.candidate })
        }
        pc.onconnectionstatechange = () => state(pc.connectionState)
        stream.getTracks().forEach((t) => pc.addTrack(t, stream))
        const offer = await pc.createOffer()
        await pc.setLocalDescription(offer)
        relay({ kind: 'offer', sdp: pc.localDescription })
    }

    ws.onopen = () => {
        ws.send(JSON.stringify({ type: 'join', room }))
        state('waiting')
    }
    ws.onmessage = async (ev) => {
        let msg
        try {
            msg = JSON.parse(ev.data)
        } catch (e) {
            return
        }
        if (msg.type === 'room-full') return state('full')
        if (msg.type === 'joined' && msg.peers >= 2) return makeOffer()
        if (msg.type === 'peer-joined') return makeOffer()
        if (msg.type === 'signal' && pc) {
            const d = msg.data
            try {
                if (d.kind === 'answer') await pc.setRemoteDescription(d.sdp)
                else if (d.kind === 'ice' && d.candidate)
                    await pc.addIceCandidate(d.candidate)
            } catch (e) {
                /* ignore */
            }
        }
    }
    ws.onerror = () => state('error')
    ws.onclose = () => state('closed')

    return {
        // Tell the desktop the phone's current device orientation so it can
        // auto-rotate the feed. Relayed over signaling (no data channel needed).
        sendOrient(orientation) {
            relay({ kind: 'orient', orientation })
        },
        // Swap the outgoing camera track (front/rear switch) with no
        // renegotiation — replaceTrack keeps the same m-line.
        async replaceVideoTrack(track) {
            if (!pc || !track) return
            const sender = pc
                .getSenders()
                .find((s) => s.track && s.track.kind === 'video')
            if (sender) await sender.replaceTrack(track)
        },
        close() {
            try {
                pc && pc.close()
            } catch (e) {
                /* ignore */
            }
            try {
                ws.close()
            } catch (e) {
                /* ignore */
            }
        },
    }
}
